import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import IncludeSideBar from "../../../Components/Sidebar/IncludeSideBar";
import { getAdvancedAnalytics } from "../../../apis/dashboard";
import "../Overview/Analytics.css";

const AdvancedAnalytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState("today");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [minSessionDuration, setMinSessionDuration] = useState("");
  const [minVideosWatched, setMinVideosWatched] = useState("");
  const [groupBy, setGroupBy] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (timeRange) {
      case "today":
        return { startDate: Math.floor(today.getTime() / 1000), endDate: Math.floor(now.getTime() / 1000) };
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(today);
        yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { startDate: Math.floor(yesterday.getTime() / 1000), endDate: Math.floor(yesterdayEnd.getTime() / 1000) };
      case "custom":
        return {
          startDate: customStartDate ? Math.floor(customStartDate.getTime() / 1000) : null,
          endDate: customEndDate ? Math.floor(customEndDate.getTime() / 1000) : null
        };
      default:
        return { startDate: null, endDate: null };
    }
  };

  const fetchAnalytics = async (page = 1) => {
    try {
      setLoading(true);
      const dateRange = getDateRange();
      
      const params = {
        page,
        limit,
      };
      
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      if (minSessionDuration) params.minSessionDuration = parseInt(minSessionDuration) * 60;
      if (minVideosWatched) params.minVideosWatched = parseInt(minVideosWatched);
      if (groupBy) params.groupBy = groupBy;

      const response = await getAdvancedAnalytics(params);
      
      setData(response?.data || []);
      setTotalPages(response?.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(1);
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchAnalytics(1);
  };

  const handleClearFilters = () => {
    setTimeRange("today");
    setCustomStartDate(null);
    setCustomEndDate(null);
    setMinSessionDuration("");
    setMinVideosWatched("");
    setGroupBy("");
    setCurrentPage(1);
    fetchAnalytics(1);
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getColumns = () => {
    if (groupBy === "country") {
      return ["Country", "Session Count", "Avg Duration", "Total Videos", "Avg Watch Time"];
    }
    if (groupBy === "city") {
      return ["City", "Session Count", "Avg Duration", "Total Videos", "Avg Watch Time"];
    }
    return ["User ID", "Name", "Country", "City", "Login Time", "Logout Time", "Duration", "Videos Watched", "Watch Time"];
  };

  const renderRow = (item, index) => {
    if (groupBy === "country" || groupBy === "city") {
      return (
        <tr key={index}>
          <td>{item._id || "N/A"}</td>
          <td>{item.sessionCount || 0}</td>
          <td>{formatDuration(item.avgSessionDuration)}</td>
          <td>{item.totalVideosWatched || 0}</td>
          <td>{formatDuration(item.avgWatchTime)}</td>
        </tr>
      );
    }
    return (
      <tr key={index}>
        <td>{item.userId || "N/A"}</td>
        <td>{item.name || "N/A"}</td>
        <td>{Array.isArray(item.country) ? item.country.join(", ") : item.country || "N/A"}</td>
        <td>{Array.isArray(item.city) ? item.city.join(", ") : item.city || "N/A"}</td>
        <td>{item.loginTime ? (item.loginTime > 1000000000000 ? new Date(item.loginTime).toLocaleString() : new Date(item.loginTime * 1000).toLocaleString()) : "N/A"}</td>
        <td>{item.logoutTime ? (item.logoutTime > 1000000000000 ? new Date(item.logoutTime).toLocaleString() : new Date(item.logoutTime * 1000).toLocaleString()) : "N/A"}</td>
        <td>{formatDuration(item.sessionDuration)}</td>
        <td>{item.videosWatched || 0}</td>
        <td>{formatDuration(item.totalWatchTime)}</td>
      </tr>
    );
  };

  return (
    <IncludeSideBar>
      <div className="advanced-page">
        <h2>Advanced Analytics</h2>

        <div className="filter-container">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ minWidth: "150px" }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="custom">Custom Date</option>
          </select>

          {timeRange === "custom" && (
            <>
              <DatePicker
                selected={customStartDate}
                onChange={(date) => setCustomStartDate(date)}
                selectsStart
                startDate={customStartDate}
                endDate={customEndDate}
                placeholderText="Start Date"
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
              />
              <DatePicker
                selected={customEndDate}
                onChange={(date) => setCustomEndDate(date)}
                selectsEnd
                startDate={customStartDate}
                endDate={customEndDate}
                placeholderText="End Date"
                minDate={customStartDate}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
              />
            </>
          )}

          <input
            type="number"
            placeholder="Min session (min)"
            value={minSessionDuration}
            onChange={(e) => setMinSessionDuration(e.target.value)}
            style={{ width: "120px" }}
            min="0"
          />

          <input
            type="number"
            placeholder="Min videos watched"
            value={minVideosWatched}
            onChange={(e) => setMinVideosWatched(e.target.value)}
            style={{ width: "140px" }}
            min="0"
          />

          <select 
            value={groupBy} 
            onChange={(e) => setGroupBy(e.target.value)}
            style={{ minWidth: "150px" }}
          >
            <option value="">No Grouping</option>
            <option value="country">Group by Country</option>
            <option value="city">Group by City</option>
          </select>

          <button onClick={handleSearch} className="primary">
            Apply
          </button>
          
          <button onClick={handleClearFilters} className="secondary">
            Clear
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading analytics...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => fetchAnalytics(currentPage)}>Retry</button>
          </div>
        ) : (
          <>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    {getColumns().map((col, idx) => (
                      <th key={idx}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.length > 0 ? (
                    data.map((item, idx) => renderRow(item, idx))
                  ) : (
                    <tr>
                      <td colSpan={getColumns().length} style={{ textAlign: "center", padding: "30px" }}>
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => { setCurrentPage(1); fetchAnalytics(1); }}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchAnalytics(p); }}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchAnalytics(p); }}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  onClick={() => { setCurrentPage(totalPages); fetchAnalytics(totalPages); }}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </IncludeSideBar>
  );
};

export default AdvancedAnalytics;
